import { EventEmitter, Injectable, Output } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { BREADCRUMBS_KEY, JUST_LOGGED_IN_KEY, LANGUAGE_KEY, STORAGE_KEY_AUTH, STORAGE_KEY_USER } from 'src/app/constants';
import { environment } from '../../../environments/environment';
import { CurrentUser } from './current-user';
import { TranslateService } from '@ngx-translate/core';
import { Translations } from '../translations';
import { TranslationsEnum } from '@api/models/translations-enum';
import { UserLoginResponse, UserProfileResponse } from '@api/models/user-login-response';
import { AuthResponse, User } from '@api/models/user';
import jwtDecode from 'jwt-decode';
import { UsersApi } from '@api/index';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private loggedInSubject = new Subject<boolean>();
  private userSubject = new Subject<User>();
  private apiPath = environment.apiBaseUrl;
  authToken: UserLoginResponse;
  previousUrl: string;
  user: CurrentUser;
  isLoggedIn$ = this.loggedInSubject.asObservable();
  user$ = this.userSubject.asObservable();

  constructor(
    private router: Router,
    private userApi: UsersApi,
    private translateService: TranslateService
  ) {
    this.checkForUser();
  }

  login(email: string, password: string): Observable<AuthResponse> {
    return this.userApi.login({ email, password }).pipe(
      switchMap((response: UserLoginResponse) => {
        this.processToken({ token: response.token, refreshToken: response.refreshToken });
        this.setLanguage(response.language ?? TranslationsEnum.English);
        this.updateUserInfo(email, response.fullName);

        if (response.customers && response.customers.length > 1) {
          return of<AuthResponse>({ multipleCustomersResponse: { multipleCustomers: true, customers: response.customers } });
        } else {
          const customerId = response.customers?.length ? response.customers[0].id : null;
          return this.userProfile(customerId).pipe(
            map(result => {
              return { user: result } as AuthResponse;
            })
          );
        }
      }),
      map((authResponse: AuthResponse) => {
        if (authResponse.user) {
          this.setLoggedInState(true);
        }
        return authResponse;
      })
    );
  }

  getUser() {
    return this.user;
  }

  formatExpireDate(dateObj: Date) {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');

    const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    return formattedDate;
  }

  register(email: string, password: string, code: string) {
    return this.userApi.register({ email, password, code });
  }

  email(code: string) {
    return this.userApi.verify({ code });
  }

  userProfile(customerId: number | null): Observable<CurrentUser> {
    return this.userApi.selectProfile(customerId).pipe(
      map((response: UserProfileResponse) => {
        this.setCurrentUser(response);
        this.processToken({ token: response.token, refreshToken: response.refreshToken });
        this.setLanguage(response.language);
        this.setLoggedInState(true);

        return this.user;
      })
    );
  }

  get userData() {
    return this.user;
  }

  logout(): void {
    this.setLoggedInState(false);
    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem(STORAGE_KEY_AUTH);
    localStorage.removeItem(LANGUAGE_KEY);
    localStorage.removeItem(BREADCRUMBS_KEY);
    this.user = null;
    this.authToken = null;
  }

  setCurrentUser(currentUser: UserProfileResponse, currentCustomerId?: number) {
    const { id, roleId, customerId, name, fullName, permissions, token, refreshToken } = currentUser;
    this.user.id = id;
    this.user.roleId = roleId;
    this.user.currentCustomerId = currentCustomerId || null;
    this.user.customerId = customerId;
    this.user.fullName = name || fullName;
    this.user.permissions = permissions;

    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(this.user));
  }

  setCustomer(currUser, currentCustomerId: number) {
    this.setCurrentUser(currUser, currentCustomerId);
    return this.user;
  }

  checkForUser(): Observable<User> {
    if (this.user) {
      return of(this.user);
    }
    const token = JSON.parse(localStorage.getItem(STORAGE_KEY_AUTH));
    if (!token || this.tokenExpired(token)) {
      this.logout();
      return of(null);
    } else {
      this.authToken = token;
      const user = JSON.parse(localStorage.getItem(STORAGE_KEY_USER));
      if (!user) {
        this.logout();
        return of(null);
      }
      this.user = user;
      return of(this.user);
    }
  }

  isLoggedIn(): boolean {
    return !!this.user;
  }

  tokenExpired(authToken?: UserLoginResponse): boolean {
    const expirationDate = new Date(authToken?.expires || this.authToken?.expires);
    const now = new Date();
    const hasExpired = expirationDate < now;
    return hasExpired;
  }

  setLoggedInState(loggedIn: boolean) {
    if (loggedIn) {
      sessionStorage.setItem(JUST_LOGGED_IN_KEY, '1');
    }
    this.loggedInSubject.next(loggedIn);
  }

  setUser(user) {
    this.userSubject.next(user);
  }

  handleUnauthorized(error: any) {
    this.logout();
    this.previousUrl = this.router.routerState.snapshot.url;
    this.router.navigateByUrl('/login');
    return throwError(() => new Error(error));
  }

  resetPassword(email?: string) {
    return this.userApi.resetPassword({ email: email || this.user?.email });
  }

  updateUserLanguage(languageId: number) {
    return this.userApi.updateLanguage({ userId: this.user.id, languageId });
  }

  processToken(response: UserLoginResponse): void {
    const decoded = jwtDecode(response.token);
    const dateObj = new Date(decoded['exp'] * 1000);
    const formattedDate = this.formatExpireDate(dateObj);

    this.authToken = {
      token: response.token,
      refreshToken: response.refreshToken,
      expires: formattedDate,
    };

    localStorage.setItem(STORAGE_KEY_AUTH, JSON.stringify(this.authToken));
  }

  setLanguage(language: TranslationsEnum) {
    localStorage.setItem(LANGUAGE_KEY, Translations[language]);
    this.translateService.use(Translations[language]);
  }

  updateUserInfo(email: string, fullName: string): void {
    this.user = { email: email, fullName: fullName };
    this.setUser(this.user);
  }
}
